import {
  type ProductImageIdentityReviewCreate,
  type ProductImageJobCreate,
  type ProductImageJobResume,
  type ProductImageReviewCreate,
  productImageIdentityReviewCreateSchema,
  productImageIdentityReviewSchema,
  productImageJobCreateSchema,
  productImageJobPageSchema,
  productImageJobResumeSchema,
  productImageJobSchema,
  productImageJobsSchema,
  productImageOverlaySchema,
  productImageReviewCreateSchema,
  productImageReviewSchema,
} from "@backchannel/tax-contracts";
import type { ApiRequest, Transport } from "./transport";

export function productImageMethods(transport: Transport) {
  const root = (catalogId: string) => `/v1/product-catalogs/${encodeURIComponent(catalogId)}`;
  const jobs = (catalogId: string) => `${root(catalogId)}/image-enrichment-jobs`;
  async function read<T>(
    schema: { parse: (body: unknown) => T },
    path: string,
    organizationId: string,
    query?: ApiRequest["query"],
    signal?: AbortSignal,
  ) {
    return schema.parse(
      (await transport({ method: "GET", path, organizationId, query, signal })).body,
    );
  }
  async function write<T>(
    schema: { parse: (body: unknown) => T },
    path: string,
    organizationId: string,
    body: unknown,
    idempotencyKey: string,
  ) {
    return schema.parse(
      (
        await transport({
          method: "POST",
          path,
          organizationId,
          body,
          idempotencyKey,
          timeoutMs: 60_000,
        })
      ).body,
    );
  }
  return {
    getProductImageIllustrationContent: async (
      catalogId: string,
      productId: string,
      revisionId: string,
      illustrationId: string,
      organizationId: string,
      signal?: AbortSignal,
    ): Promise<ArrayBuffer> => {
      const result = await transport({
        method: "GET",
        path: `${root(catalogId)}/products/${encodeURIComponent(productId)}/image-illustrations/${encodeURIComponent(illustrationId)}/content`,
        organizationId,
        query: { revisionId },
        signal,
        responseType: "arrayBuffer",
      });
      if (!(result.body instanceof ArrayBuffer)) {
        throw new TypeError("The generated product illustration response was not binary");
      }
      return result.body;
    },
    reviewProductImageIdentity: (
      catalogId: string,
      productId: string,
      organizationId: string,
      body: ProductImageIdentityReviewCreate,
      key: string,
    ) =>
      write(
        productImageIdentityReviewSchema,
        `${root(catalogId)}/products/${encodeURIComponent(productId)}/image-identity-reviews`,
        organizationId,
        productImageIdentityReviewCreateSchema.parse(body),
        key,
      ),
    getProductImageOverlay: (
      catalogId: string,
      productId: string,
      revisionId: string,
      organizationId: string,
      signal?: AbortSignal,
    ) =>
      read(
        productImageOverlaySchema,
        `${root(catalogId)}/products/${encodeURIComponent(productId)}/image-overlay`,
        organizationId,
        { revisionId },
        signal,
      ),
    listProductImageJobs: (
      catalogId: string,
      organizationId: string,
      query: { revisionId?: string; productId?: string; limit?: number },
      signal?: AbortSignal,
    ) => read(productImageJobsSchema, jobs(catalogId), organizationId, query, signal),
    getProductImageJob: (
      catalogId: string,
      jobId: string,
      organizationId: string,
      query: { cursor?: string; limit?: number },
      signal?: AbortSignal,
    ) =>
      read(
        productImageJobPageSchema,
        `${jobs(catalogId)}/${encodeURIComponent(jobId)}`,
        organizationId,
        query,
        signal,
      ),
    createProductImageJob: (
      catalogId: string,
      organizationId: string,
      body: ProductImageJobCreate,
      key: string,
    ) =>
      write(
        productImageJobSchema,
        jobs(catalogId),
        organizationId,
        productImageJobCreateSchema.parse(body),
        key,
      ),
    resumeProductImageJob: (
      catalogId: string,
      jobId: string,
      organizationId: string,
      body: ProductImageJobResume,
      key: string,
    ) =>
      write(
        productImageJobSchema,
        `${jobs(catalogId)}/${encodeURIComponent(jobId)}/resume`,
        organizationId,
        productImageJobResumeSchema.parse(body),
        key,
      ),
    reviewProductImageCandidate: (
      catalogId: string,
      candidateId: string,
      organizationId: string,
      body: ProductImageReviewCreate,
      key: string,
    ) =>
      write(
        productImageReviewSchema,
        `${root(catalogId)}/image-candidates/${encodeURIComponent(candidateId)}/reviews`,
        organizationId,
        productImageReviewCreateSchema.parse(body),
        key,
      ),
  };
}
